import React, { useContext, useEffect, useRef, useState } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SeoHelmet from '../../common/SeoHelmet';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import MuiSnackbar from '../../common/MuiSnackbar';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import LabelContainer from './LabelContainer';
import InputLayer from './InputLayer';
import LayeredSoilProfileSvg from './LayeredSoilProfileSvg';
import { useCheckMissingParamsForLayeredCalculations } from '../../../hooks/useCheckMissingParamsForLayeredCalculations';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const LayeredSoil = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    resultsArray,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const [showIterationDetails, setShowIterationDetails] = useState(false);
  const [expandedIterationRows, setExpandedIterationRows] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  const sketchContainerRef = useRef(null);

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [foundationDepth, setFoundationDepth] = useState('');
  const [foundationWidth, setFoundationWidth] = useState('');
  const [layersFront, setLayersFront] = useState([]);
  const [svgLayersFront, setSvgLayersFront] = useState([]);
  const [svgFoundationDepth, setSvgFoundationDepth] = useState('');
  const [svgFoundationWidth, setSvgFoundationWidth] = useState('');

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setFoundationDepth(inputs.foundationDepth);
    setFoundationWidth(inputs.foundationWidth);
    setLayersFront(inputs.layersFront);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    foundationDepth,
    foundationWidth,
    layersFront,
  };

  //ADD LAYER
  const addLayer = (e) => {
    e.preventDefault();
    let z1;
    if (layersFront.length === 0) {
      z1 = foundationDepth;
    } else if (layersFront.length > 0) {
      z1 = layersFront[layersFront.length - 1].z2;
    }

    const layer = {
      id: layersFront.length + 1,
      formation: '',
      z1: z1,
      z2: '',
      hi: '',
      c: '',
      phi: '',
    };

    const newLayersFront = layersFront.slice();
    newLayersFront.push(layer);
    setLayersFront(newLayersFront);
  };

  //REMOVE LAYER
  const removeLayer = (e) => {
    e.preventDefault();
    const newLayersFront = layersFront.slice();
    newLayersFront.pop();
    setLayersFront(newLayersFront);
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);
    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.foundationDepth || calculationInput.foundationDepth === 0) &&
      (calculationInput.foundationWidth || calculationInput.foundationWidth === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [
    setCalculateButtonDisabled,
    setSaveButtonDisabled,
    calculationInput.name,
    calculationInput.foundationDepth,
    calculationInput.foundationWidth,
  ]);

  //check layer mising params and handle calculate button disabled/enabled status
  const doLayersHaveMissingParams = useCheckMissingParamsForLayeredCalculations(layersFront);
  useEffect(() => {
    setCalculateButtonDisabled(doLayersHaveMissingParams);
    setSaveButtonDisabled(doLayersHaveMissingParams);
  }, [doLayersHaveMissingParams, layersFront, setCalculateButtonDisabled, setSaveButtonDisabled]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    calculationInput.foundationDepth,
    calculationInput.foundationWidth,
    JSON.stringify(calculationInput.layersFront),
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setFoundationDepth('');
    setFoundationWidth('');
    setLayersFront([]);
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/layeredSoil',
    LOADONE: `/api/v1/calculations/layeredSoil/loadOne/`,
    LOADALL: '/api/v1/calculations/layeredSoil/loadAll',
    UPDATE: `/api/v1/calculations/layeredSoil/update/`,
    SAVE: '/api/v1/calculations/layeredSoil/save',
    CHECKUNIQUENAME: `/api/v1/calculations/layeredSoil/checkUniqueName/`,
    DELETE: '/api/v1/calculations/layeredSoil/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  //CONVERT RESPONSE OBJECT TO ARRAYS FOR PRINTING PURPOSES
  const outputs = result?.outputs || result;
  const canUserSeeCalculationSteps = Boolean(outputs?.canUserSeeCalculationSteps);
  const calculationProcedure = outputs?.calculationProcedure;
  const procedureTitle = calculationProcedure?.title || 'Layered Soil Iterative Procedure';
  const procedureDescription =
    calculationProcedure?.description ||
    'The method iteratively updates average friction angle and cohesion over the effective depth until convergence.';
  const procedureTolerance = calculationProcedure?.tolerance;
  const maxIterations = calculationProcedure?.maxIterations;
  const didConverge = calculationProcedure?.didConverge;
  const iterationDetails = Array.isArray(calculationProcedure?.iterationDetails) ? calculationProcedure.iterationDetails : [];
  const finalValues = calculationProcedure?.finalValues || null;
  const hEffectiveFinal = finalValues?.hEffectiveFinal ?? null;
  const hasLayerOrderError = Array.isArray(layersFront)
    ? layersFront.some((layer, index) => {
        const z1 = index === 0 ? Number(foundationDepth) : Number(layersFront?.[index - 1]?.z2);
        const z2 = Number(layer?.z2);
        if (!Number.isFinite(z1) || !Number.isFinite(z2)) return false;
        return z2 <= z1;
      })
    : false;

  useEffect(() => {
    if (!hasLayerOrderError) return;
    setCalculateButtonDisabled(true);
    setSaveButtonDisabled(true);
  }, [hasLayerOrderError, setCalculateButtonDisabled, setSaveButtonDisabled]);

  useEffect(() => {
    if (hasLayerOrderError) return;
    setSvgLayersFront(layersFront);
    setSvgFoundationDepth(foundationDepth);
    setSvgFoundationWidth(foundationWidth);
  }, [layersFront, foundationDepth, foundationWidth, hasLayerOrderError]);

  useEffect(() => {
    setShowIterationDetails(false);
    setExpandedIterationRows({});
  }, [showOutput, iterationDetails.length]);

  const toggleIterationRow = (iteration) => {
    setExpandedIterationRows((prev) => ({ ...prev, [iteration]: !prev[iteration] }));
  };

  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user?.role === 'basic' || user?.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const sketchPngBase64 = await svgContainerToPngBase64(sketchContainerRef.current, { scale: 2 });
      const payload = {
        ...calculationInput,
        sketchPngBase64,
      };

      const res = await fetch('/api/v1/pdf/layeredSoil', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-layered-soil-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
    }
  };

  // RETURN INPUT AND OUTPUT JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Layered Soils — Average c and φ Calculator"
        description="Compute thickness-weighted average cohesion (c̄) and friction angle (φ̄) for layered soils using foundation depth/width and per-layer inputs (z1, z2, c, φ)."
        url="https://www.geocalc.app/calculations/layered-soil"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Layered Soils — Average c and φ Calculator', url: 'https://www.geocalc.app/calculations/layered-soil' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user?.role} />
        <div className="right-calculation layeredSoilPage">
          <section className="input layeredSoilInput">
            <h1>Average cohesion and average friction angle calculations for layered soils</h1>
            {isLoadingLoad && (
              <div className="loadingAbsolute">
                <CircularProgress size={30} />
              </div>
            )}
            <form className="layeredSoilPrimaryForm">
              <TextField
                sx={{ width: 200 }}
                size="small"
                label="Name"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: `${MAX_NAME_LENGTH}`,
                }}
                value={name}
                onChange={handleChangeName}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 200 }}
                size="small"
                label={
                  <span>
                    D<sub>f</sub> (m)
                  </span>
                }
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Foundation Depth (m). Measured from ground surface."
                        arrow
                        placement="bottom"
                        TransitionComponent={Zoom}
                      >
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={foundationDepth}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Foundation Depth', setFoundationDepth)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
              <TextField
                sx={{ width: 200 }}
                size="small"
                label={'B (m)'}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Foundation Width (m)" arrow placement="bottom" TransitionComponent={Zoom}>
                        <InfoTwoToneIcon fontSize={'string'} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={foundationWidth}
                onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Foundation Width', setFoundationWidth)}
                disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
              />
            </form>
            <div className="layeredSoilLayerContainer horizontalScroll">
              {layersFront.length > 0 && <LabelContainer />}
              {layersFront?.map((layer, index) => {
                if (index === 0) {
                  layersFront[index].z1 = foundationDepth;
                }
                if (index !== layersFront.length - 1) {
                  layersFront[index + 1].z1 = layersFront[index].z2;
                }
                layer.hi = layer.z2 - layer.z1;
                const updateLayer = (key, value) => {
                  const newLayer = {
                    ...layer,
                  };
                  newLayer[key] = value;
                  const newLayers = layersFront.slice();
                  newLayers.splice(index, 1, newLayer);
                  setLayersFront(newLayers);
                };
                return (
                  <InputLayer
                    key={index}
                    layer={layer}
                    updateLayer={updateLayer}
                    isLoadingCalculate={isLoadingCalculate}
                    isLoadingLoad={isLoadingLoad}
                    isLoadingSave={isLoadingSave}
                  />
                );
              })}
            </div>
            <div className="layeredSoilHelperTextRow">
              {hasLayerOrderError && (
                <span className="layeredSoilHelperText">Fix highlighted z2 values: each z2 must be greater than z1.</span>
              )}
            </div>
            <div className="Vs30SiteClassEc8ButtonContainer">
              <Button
                variant="outlined"
                onClick={removeLayer}
                disabled={isLoadingCalculate || isLoadingSave || isLoadingLoad || layersFront.length < 1}
              >
                Remove Layer
              </Button>
              <Button
                variant="outlined"
                onClick={addLayer}
                disabled={isLoadingCalculate || isLoadingSave || isLoadingLoad || layersFront.length > 39}
              >
                Add Layer
              </Button>
            </div>
            <LayeredSoilProfileSvg
              layers={svgLayersFront}
              foundationDepth={svgFoundationDepth}
              foundationWidth={svgFoundationWidth}
              hEffective={showOutput ? hEffectiveFinal : null}
            />
            <div
              ref={sketchContainerRef}
              style={{
                position: 'absolute',
                left: -99999,
                top: -99999,
                width: 800,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <LayeredSoilProfileSvg
                layers={svgLayersFront}
                foundationDepth={svgFoundationDepth}
                foundationWidth={svgFoundationWidth}
                hEffective={showOutput ? hEffectiveFinal : null}
                variant="pdf-light"
              />
            </div>
            <div className="buttonContainer Vs30SiteClassEc8CalcSaveButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
                layersFrontLength={layersFront.length}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px', whiteSpace: 'nowrap' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF...
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>
              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
                layersFrontLength={layersFront.length}
              />
            </div>
            {isLoadingCalculate && (
              <div className="loadingAbsolute">
                <CircularProgress />
              </div>
            )}
          </section>

          {showOutput && (
            <section className="output layeredSoilOutputSection loadingRelative ">
              <h2>Output </h2>
              <Box sx={{ my: 1, textAlign: 'justify' }}>
                <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
                  <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
                    {procedureTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                    {procedureDescription}
                  </Typography>
                  <Alert
                    icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
                    severity="success"
                    variant="outlined"
                    sx={{
                      backgroundColor: 'rgba(46,125,50,0.04)',
                      alignItems: 'flex-start',
                      '& .MuiAlert-icon': { alignSelf: 'flex-start', mt: 0.25, minWidth: 24 },
                      '& .MuiAlert-action': {
                        alignSelf: 'flex-start',
                        mt: 0.25,
                        width: 32,
                        justifyContent: 'center',
                        p: 0,
                        ml: 1,
                      },
                      '& .MuiAlert-message': { width: '100%' },
                    }}
                    action={
                      canUserSeeCalculationSteps ? (
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={() => setShowIterationDetails((v) => !v)}
                          sx={{ width: 32, height: 32 }}
                        >
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: showIterationDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                        </IconButton>
                      ) : (
                        <Tooltip
                          title={
                            <span>
                              Upgrade to unlock calculation details.{' '}
                              <Link
                                href="/pricing"
                                target="_blank"
                                rel="noopener"
                                style={{ color: '#90caf9', textDecoration: 'underline' }}
                              >
                                See plans
                              </Link>
                            </span>
                          }
                          leaveDelay={500}
                          arrow
                        >
                          <LockOutlinedIcon
                            sx={{ fontSize: 20, color: 'text.disabled', cursor: 'not-allowed', mr: 1, mt: 0.5 }}
                          />
                        </Tooltip>
                      )
                    }
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: canUserSeeCalculationSteps ? 0.5 : 0 }}>
                      {finalValues ? (
                        didConverge ? (
                          <>
                            Converged in {finalValues.iterationCount} iterations. H<sub>e</sub> ={' '}
                            {finalValues.hEffectiveFinal?.toFixed?.(3)} m, φ<sub>avg</sub> ={' '}
                            {finalValues.phiAverageFinal?.toFixed?.(3)}°, c<sub>avg</sub> ={' '}
                            {finalValues.cAverageFinal?.toFixed?.(3)} kPa. Convergence tolerance: ΔH
                            <sub>e</sub> ≤ {Number(procedureTolerance).toExponential(1)} m.
                          </>
                        ) : (
                          <>
                            Maximum iteration number reached (max = {maxIterations}), no convergence. H<sub>e</sub> ={' '}
                            {finalValues.hEffectiveFinal?.toFixed?.(3)} m, φ<sub>avg</sub> ={' '}
                            {finalValues.phiAverageFinal?.toFixed?.(3)}°, c<sub>avg</sub> ={' '}
                            {finalValues.cAverageFinal?.toFixed?.(3)} kPa. Convergence tolerance: ΔH
                            <sub>e</sub> ≤ {Number(procedureTolerance).toExponential(1)} m.
                          </>
                        )
                      ) : (
                        'Iteration results are available below.'
                      )}
                    </Typography>
                    {canUserSeeCalculationSteps && (
                      <Collapse in={showIterationDetails} unmountOnExit sx={{ width: '100%' }}>
                        {iterationDetails.length > 0 && (
                          <Box sx={{ mt: 1, overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                              <thead>
                                <tr>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    Iteration
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    H<sub>e</sub> used (m)
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    φ<sub>avg</sub> (deg)
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    c<sub>avg</sub> (kPa)
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    H<sub>e</sub> updated (m)
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    ΔH<sub>e</sub> (m)
                                  </th>
                                  <th style={{ borderBottom: '1px solid #999', textAlign: 'center', padding: '6px 8px' }}>
                                    Details
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {iterationDetails.map((row, rowIndex) => {
                                  const isRowOpen = Boolean(expandedIterationRows[row.iteration]);
                                  const isLastIteration = rowIndex === iterationDetails.length - 1;
                                  const isContinuing =
                                    procedureTolerance != null && row.difference != null
                                      ? row.difference > procedureTolerance
                                      : false;
                                  return (
                                    <React.Fragment key={`iter-detail-${row.iteration}`}>
                                      <tr>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.iteration}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.hEffective?.toFixed?.(4)}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.phiAverage?.toFixed?.(4)}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.cAverage?.toFixed?.(4)}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.hEffectiveUpdated?.toFixed?.(4)}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '6px 8px', textAlign: 'center' }}>
                                          {row.difference?.toExponential?.(3)}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #ddd', padding: '0 4px', textAlign: 'center' }}>
                                          <IconButton
                                            size="small"
                                            color="inherit"
                                            onClick={() => toggleIterationRow(row.iteration)}
                                            sx={{ width: 28, height: 28 }}
                                          >
                                            <ExpandMoreIcon
                                              fontSize="small"
                                              sx={{
                                                transform: isRowOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.15s',
                                              }}
                                            />
                                          </IconButton>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td colSpan={7} style={{ borderBottom: '1px solid #ddd', padding: 0 }}>
                                          <Collapse in={isRowOpen} unmountOnExit>
                                            <Box
                                              sx={{
                                                p: 1,
                                                backgroundColor: 'rgba(0,0,0,0.04)',
                                                borderLeft: '1px solid rgba(0,0,0,0.08)',
                                                borderRight: '1px solid rgba(0,0,0,0.08)',
                                                borderRadius: 0,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mb: 0.75, textAlign: 'center', fontWeight: 600 }}
                                              >
                                                Details of iteration #{row.iteration}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                                                D<sub>f</sub> = {row.foundationDepth?.toFixed?.(4)} m, calculation depth from
                                                ground = {row.calculationDepthFromGround?.toFixed?.(4)} m
                                              </Typography>

                                              <Box sx={{ overflowX: 'auto', mb: 1 }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                  <thead>
                                                    <tr>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        Layer
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        z<sub>1</sub> (m)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        z<sub>2</sub> (m)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        h<sub>i,used</sub> (m)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        Type
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        φ (deg)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        tan(φ)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        c (kPa)
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        tan(φ)h<sub>i,used</sub>
                                                      </th>
                                                      <th
                                                        style={{
                                                          borderBottom: '1px solid #bbb',
                                                          padding: '5px 6px',
                                                          textAlign: 'center',
                                                        }}
                                                      >
                                                        ch<sub>i,used</sub>
                                                      </th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {(row.layerContributions || []).map((item, idx) => (
                                                      <tr key={`iter-${row.iteration}-layer-${idx}`}>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.layerId}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.z1?.toFixed?.(2)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.z2?.toFixed?.(2)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.hiUsed?.toFixed?.(2)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.extended ? 'Extended' : item.partial ? 'Partial' : 'Full'}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.phi?.toFixed?.(2)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.tanPhi?.toFixed?.(3)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.c?.toFixed?.(2)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.phiContribution?.toFixed?.(3)}
                                                        </td>
                                                        <td
                                                          style={{
                                                            borderBottom: '1px solid #e2e2e2',
                                                            padding: '5px 6px',
                                                            textAlign: 'center',
                                                          }}
                                                        >
                                                          {item.cContribution?.toFixed?.(3)}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </Box>

                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                h<sub>&phi;</sub> = &Sigma;[tan(&phi;<sub>i</sub>) * h<sub>i,used</sub>] ={' '}
                                                {row.sumPhiContribution?.toFixed?.(6)}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                h<sub>c</sub> = &Sigma;[c<sub>i</sub> * h<sub>i,used</sub>] ={' '}
                                                {row.sumCContribution?.toFixed?.(6)}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                &phi;<sub>avg</sub> = atan(h<sub>&phi;</sub> / H<sub>e</sub>) = atan(
                                                {row.sumPhiContribution?.toFixed?.(6)} / {row.hEffective?.toFixed?.(6)}) ={' '}
                                                {row.phiAverage?.toFixed?.(6)}&deg;
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                c<sub>avg</sub> = h<sub>c</sub> / H<sub>e</sub> ={' '}
                                                {row.sumCContribution?.toFixed?.(6)} / {row.hEffective?.toFixed?.(6)} ={' '}
                                                {row.cAverage?.toFixed?.(6)} kPa
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                H<sub>e,new</sub> = 0.5 * B * tan(45 + &phi;<sub>avg</sub>/2) = 0.5 *{' '}
                                                {row.foundationWidth?.toFixed?.(6)} * tan(45 + {row.phiAverage?.toFixed?.(6)}/2) ={' '}
                                                {row.hEffectiveUpdated?.toFixed?.(6)} m
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ display: 'block', fontFamily: 'monospace' }}
                                              >
                                                &Delta;H<sub>e</sub> = |H<sub>e,new</sub> - H<sub>e,used</sub>| = |
                                                {row.hEffectiveUpdated?.toFixed?.(6)} - {row.hEffective?.toFixed?.(6)}| ={' '}
                                                {row.difference?.toExponential?.(6)} m
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 0.75, fontStyle: 'italic', textAlign: 'center' }}
                                              >
                                                {isLastIteration ? (
                                                  <>
                                                    Note: &Delta;H<sub>e</sub> = {row.difference?.toExponential?.(3)} m{' '}
                                                    {isContinuing ? '>' : '\u2264'} {Number(procedureTolerance).toExponential(1)}{' '}
                                                    m = tolerance.{' '}
                                                    {didConverge
                                                      ? `Convergence achieved at iteration #${row.iteration}.`
                                                      : `Maximum iteration number reached (max = ${maxIterations}), no convergence.`}
                                                  </>
                                                ) : (
                                                  <>
                                                    Note: &Delta;H<sub>e</sub> = {row.difference?.toExponential?.(3)} m {'>'}{' '}
                                                    {Number(procedureTolerance).toExponential(1)} m = tolerance, so continuing
                                                    with iteration #{row.iteration + 1}.
                                                  </>
                                                )}
                                              </Typography>
                                            </Box>
                                          </Collapse>
                                        </td>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Box>
                        )}
                      </Collapse>
                    )}
                  </Alert>
                </Alert>
              </Box>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const LayeredSoilWithProvider = () => {
  return (
    <CalculationContextProvider>
      <LayeredSoil />
    </CalculationContextProvider>
  );
};
export default LayeredSoilWithProvider;
